import type { CSSProperties } from 'react';

import { TEXT_COLOR_ROLES } from './tokens';

/** Sentinel meaning "change nothing"; descendants keep whatever the page or theme provides. */
export const INHERIT_COLOR = 'inherit';

/**
 * Text colour roles a container may push onto everything inside it.
 *
 * Deliberately excludes the `text` role: it resolves to `--dcm-color-text-primary`, which is the
 * very property this override writes, so choosing it would make the custom property reference
 * itself. A self-referential custom property is invalid at computed-value time, and the text would
 * silently fall back to `inherit` rather than the theme colour.
 */
export const CONTAINER_TEXT_COLORS = {
  secondary: TEXT_COLOR_ROLES.secondary,
  muted: TEXT_COLOR_ROLES.muted,
  inverted: TEXT_COLOR_ROLES.inverted,
} as const;

export type ContainerTextColor = keyof typeof CONTAINER_TEXT_COLORS | typeof INHERIT_COLOR;

export const CONTAINER_TEXT_COLOR_OPTIONS = [
  { label: 'Inherit', value: INHERIT_COLOR },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Muted', value: 'muted' },
  { label: 'Inverted', value: 'inverted' },
] as const;

export const CONTAINER_TEXT_COLOR_AI_SCHEMA = {
  type: 'string',
  enum: [INHERIT_COLOR, ...Object.keys(CONTAINER_TEXT_COLORS)],
} as const;

/**
 * Builds the scoped `--dcm-*` override a container writes onto its own wrapper.
 *
 * Leaf components emit a `var()` *reference* rather than a resolved colour — Heading sets
 * `--cms-heading-color: var(--dcm-color-text-primary, #485055)`. Redefining that property here
 * therefore re-points every descendant through the ordinary CSS cascade, with no changes to those
 * components at all. This is the mechanism a future named colour-scheme feature reuses.
 *
 * Returns an empty object for `inherit` or an unrecognised persisted value, so saved pages that
 * predate this control (or carry a renamed key) are left untouched rather than restyled.
 */
export function containerColorStyle(textColor: string | undefined): CSSProperties {
  if (!textColor || !Object.prototype.hasOwnProperty.call(CONTAINER_TEXT_COLORS, textColor)) return {};

  return {
    '--dcm-color-text-primary': CONTAINER_TEXT_COLORS[textColor as keyof typeof CONTAINER_TEXT_COLORS],
  } as CSSProperties;
}
